import { useContext, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { IntervalType } from '@/api/Metrics/Types/MetricsDto'
import { AuthSessionContext } from '@/utils/AuthContext'
import { hasPermission } from '@/utils/permissionsValidator'
import { Formik } from 'formik'

import DashboardForm, { DashboardFormProps } from './DashboardForm'
import { DashboardPage } from './DashboardPage'

const Dashboard = () => {
  const [filters, setFilters] = useState<DashboardFormProps | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  useEffect(() => {
    return () => {
      setFilters(null)
      setLastUpdated(null)
      localStorage.removeItem('dashboard_filters')
      localStorage.removeItem('dashboard_lastUpdated')
    }
  }, [])
  const metricsFilters = useMemo(() => {
    if (!filters) return null

    const [start, end] = filters.dateRange || []

    let intervalType = IntervalType.Daily

    if (start && end) {
      const diffInMs = end.getTime() - start.getTime()
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

      intervalType = diffInDays > 365 ? IntervalType.Monthly : IntervalType.Daily
    }

    return {
      introducingBrokerIds: filters.brokers.length ? filters.brokers.join(',') : undefined,
      countryIds: filters.country.length ? filters.country.join(',') : undefined,
      customDateRange:
        start && end ? ([start.toISOString(), end.toISOString()] as [string, string]) : undefined,
      intervalType,
    }
  }, [filters?.brokers, filters?.country, filters?.dateRange])
  useEffect(() => {
    const savedFilters = localStorage.getItem('dashboard_filters')
    const savedTimestamp = localStorage.getItem('dashboard_lastUpdated')

    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters)

        if (parsed.dateRange) {
          parsed.dateRange = [
            parsed.dateRange[0] ? new Date(parsed.dateRange[0]) : null,
            parsed.dateRange[1] ? new Date(parsed.dateRange[1]) : null,
          ]
        }

        setFilters(parsed)
      } catch {
        console.error('error')
      }
    }

    if (savedTimestamp) {
      setLastUpdated(new Date(savedTimestamp))
    }
  }, [])
  if (!hasPermission(permissions, PermissionsEnum.CanViewPAMDashboard)) {
    return (
      <div className='flex h-full flex-col items-center justify-center text-muted-foreground'>
        <p className='text-lg font-medium'>Welcome to Tickmill Backoffice</p>
        <p className='text-sm'>Select a section from the navigation to get started.</p>
      </div>
    )
  }

  return (
    <>
      <Formik<DashboardFormProps>
        enableReinitialize
        initialValues={
          filters ?? {
            brokers: [],
            dateRange: [null, null],
            country: [],
          }
        }
        onSubmit={(values) => {
          setFilters(values)
          setLastUpdated(new Date())
          setFiltersOpen(false)

          localStorage.setItem('dashboard_filters', JSON.stringify(values))
          localStorage.setItem('dashboard_lastUpdated', new Date().toISOString())
        }}
      >
        {(formik) => (
          <DashboardForm
            {...formik}
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            lastUpdated={lastUpdated}
            onResetFilters={() => {
              setFilters(null)
              localStorage.removeItem('dashboard_filters')
              localStorage.removeItem('dashboard_lastUpdated')
            }}
          />
        )}
      </Formik>
      <DashboardPage
        filtersOpen={filtersOpen}
        filters={metricsFilters}
        onOpenFilters={() => setFiltersOpen(true)}
      />{' '}
    </>
  )
}
export const DashboardLayout = () => {
  return (
    <>
      <Outlet />
    </>
  )
}
export default Dashboard
